import { Grid, makeStyles } from '@material-ui/core'
import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BaseWidget from '../common/BaseWidget'
import NeedsAttentionHeader from './NeedsAttentionHeader'
import NeedsAttentionPatientItem from './NeedsAttentionPatientItem'
import moment from 'moment'
import NeedsAttentionSkeleton from './NeedsAttentionSkeleton'
import NeedsAttentionEmptyState from './NeedsAttentionEmptyState'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '98.5%',
    paddingBottom: 0
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '98.5%',
    paddingBottom: 0
  },
  header: {
    flexShrink: 0,
    padding: 20,
    borderBottom: '1px solid #E9E9E9'
  },
  patientsList: {
    flexGrow: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    transition: 'opacity 0.3s ease-in-out',
    opacity: 1,
    '&.fading': {
      opacity: 0.5
    }
  },
  patientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    height: 90,
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9',
    '&:last-child': {
      borderBottom: 'none'
    }
  }
}))

const NeedsAttention = ({ patients = [], isLoading }) => {
  const classes = useStyles()
  const [selectedReasons, setSelectedReasons] = useState([])
  const [isFading, setIsFading] = useState(false)

  const { data: statuses, isLoading: isStatusesLoading } = useSelector(state => state.treatmentReducer.statuses)

  const filteredPatients = useMemo(() => {
    let sortedPatients = [...patients]
      .sort((patient1, patient2) => {
        return moment(patient1?.needsAttentionData?.createdAt).isAfter(moment(patient2?.needsAttentionData?.createdAt))
          ? -1
          : 1
      })
      .filter(patient => !!patient.needsAttentionData?.reason)

    if (selectedReasons.length === 0) {
      return sortedPatients
    }
    return sortedPatients.filter(patient => selectedReasons.includes(patient.needsAttentionData.reason))
  }, [patients, selectedReasons])

  const getPatientStatusObject = useCallback(
    (statusKey, program) => statuses[program]?.find(statusItem => statusItem.key === statusKey),
    [statuses]
  )

  const needsAttentionCountsByReason = useMemo(() => _.countBy(patients, 'needsAttentionData.reason'), [patients])

  useEffect(() => {
    setIsFading(true)
    const timeout = setTimeout(() => setIsFading(false), 300)
    return () => clearTimeout(timeout)
  }, [selectedReasons])

  return isLoading || isStatusesLoading ? (
    <NeedsAttentionSkeleton />
  ) : patients.length === 0 ? (
    <BaseWidget className={classes.emptyStateContainer} carousel={false}>
      <NeedsAttentionEmptyState />
    </BaseWidget>
  ) : (
    <BaseWidget id="needs-attention-patients" className={classes.root} carousel={false}>
      <Grid item className={classes.header}>
        <NeedsAttentionHeader
          total={patients.length}
          countsByReasons={needsAttentionCountsByReason}
          selectedReasons={selectedReasons}
          setSelectedReasons={setSelectedReasons}
        />
      </Grid>
      <Grid item className={`${classes.patientsList} ${isFading ? 'fading' : ''}`}>
        {filteredPatients.map((patient, index) => (
          <NeedsAttentionPatientItem
            key={index}
            patientId={patient.id}
            patientName={patient.name}
            profilePicture={patient.profilePicture}
            status={getPatientStatusObject(patient.statusKey, patient.program)}
            needsAttentionData={patient.needsAttentionData}
          />
        ))}
      </Grid>
    </BaseWidget>
  )
}

export default NeedsAttention
